import React from 'react';
import { Plus } from 'lucide-react';
import { fmtDay } from './helpers';

export default function CalCell({ date, plans, isToday, isSelected, onClick, onAddPlan, projects }) {
  const byProject = {};
  plans.forEach(p => {
    if (!byProject[p.project_id]) {
      const proj = projects?.find(pr => pr.id === p.project_id);
      byProject[p.project_id] = { name: p.project_name, unit: p.planning_unit, seatPlaced: 0, seatPlacedDone: 0, sets: 0, setsDone: 0, count: 0, ordered: proj?.ordered_quantity || 0, sr_number: proj?.sr_number || '', setNums: [] };
    }
    const spu = p.seats_per_unit || 0;
    if (p.planning_unit === 'seat') {
      byProject[p.project_id].seatPlaced += (p.planned_quantity || 0) * spu;
      byProject[p.project_id].seatPlacedDone += (p.completed_quantity || 0) * spu;
    } else {
      byProject[p.project_id].sets += p.planned_quantity || 0;
      byProject[p.project_id].setsDone += p.completed_quantity || 0;
    }
    byProject[p.project_id].count++;
    if (p.set_numbers) byProject[p.project_id].setNums.push(p.set_numbers);
    if (p.set_label) byProject[p.project_id].setNums.push(`#${p.set_number}:${p.set_label}`);
  });
  const entries = Object.entries(byProject).filter(([, v]) => v.seatPlaced > 0 || v.sets > 0 || v.count > 0);

  return (
    <div onClick={() => onClick(date)} data-testid={`cal-cell-${date}`}
      className={`cursor-pointer min-h-[120px] rounded-lg border transition-all hover:shadow-sm hover:border-slate-300
        ${isToday && isSelected ? 'border-blue-500 bg-blue-50/40 ring-2 ring-blue-400' : isSelected ? 'border-indigo-400 bg-indigo-50/30 ring-2 ring-indigo-300' : isToday ? 'border-blue-400 bg-blue-50/30 ring-1 ring-blue-200' : 'border-slate-200 bg-white'}`}>
      <div className={`flex items-center justify-between px-2 py-1 border-b ${isToday ? 'border-blue-100' : isSelected ? 'border-indigo-100' : 'border-slate-50'}`}>
        <span className={`text-xs font-bold ${isToday ? 'text-blue-600' : 'text-slate-600'}`}>{fmtDay(date)}</span>
        <div className="flex items-center gap-1">
          {entries.length > 0 && <span className="text-[9px] bg-slate-100 text-slate-500 px-1 rounded">{entries.length}</span>}
          {onAddPlan && (
            <button onClick={(e) => { e.stopPropagation(); onAddPlan(date); }}
              className="w-4 h-4 flex items-center justify-center rounded bg-blue-500 hover:bg-blue-600 text-white transition-colors" data-testid={`add-plan-${date}`}>
              <Plus className="w-2.5 h-2.5" />
            </button>
          )}
        </div>
      </div>
      <div className="p-1 space-y-0.5">
        {entries.slice(0, 4).map(([pid, v]) => {
          const isTrain = v.unit === 'seat';
          const val = isTrain ? v.seatPlaced : v.sets;
          const done = isTrain ? v.seatPlacedDone : v.setsDone;
          const pct = val > 0 ? Math.round((done / val) * 100) : 0;
          return (
            <div key={pid} className="bg-slate-50 rounded px-1.5 py-0.5">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-semibold text-slate-700 truncate">
                  {v.name}
                  {v.sr_number && <span className="text-[8px] text-blue-500 font-medium ml-1">{v.sr_number}</span>}
                </span>
                <span className="text-[9px] text-slate-500">
                  {isTrain ? `${done}/${val} sp` : `${done}/${val} sets`}
                </span>
              </div>
              {!isTrain && v.ordered > 0 && (
                <div className="text-[8px] text-violet-500 font-medium">Order: {v.ordered}</div>
              )}
              {v.setNums.length > 0 && (
                <div className="text-[8px] text-indigo-600 font-bold truncate" title={v.setNums.join(', ')}>{v.setNums.join(', ')}</div>
              )}
              <div className="w-full h-1 bg-slate-200 rounded-full mt-0.5">
                <div className={`h-full rounded-full ${pct >= 100 ? 'bg-emerald-500' : pct > 0 ? 'bg-amber-400' : 'bg-slate-200'}`}
                  style={{ width: `${Math.min(pct, 100)}%` }} />
              </div>
            </div>
          );
        })}
        {entries.length > 4 && <p className="text-[9px] text-slate-400 text-center">+{entries.length - 4} more</p>}
      </div>
    </div>
  );
}
